import { Component } from 'react'
import { useLocation } from 'react-router-dom'

// Last-resort net around <Routes>: without it any uncaught error unmounts the whole app. One silent
// retry per navigation visit (e.g. a map cleanup throwing while leaving ARV); a second error in the
// same visit shows the plain fallback; any navigation starts a new visit. See README.md here.
class Boundary extends Component {
  state = { hasError: false, gaveUp: false, retriedVisit: null }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  // An error on the very first render lands in componentDidMount, every later one in componentDidUpdate.
  componentDidMount() {
    this.settle()
  }

  componentDidUpdate(prevProps) {
    const moved = prevProps.visit !== this.props.visit
    if (moved && this.state.gaveUp) {
      this.setState({ hasError: false, gaveUp: false, retriedVisit: null })
    } else if (this.state.hasError && !this.state.gaveUp) {
      this.settle()
    } else if (moved && this.state.retriedVisit !== null) {
      this.setState({ retriedVisit: null })
    }
  }

  settle() {
    const { hasError, gaveUp, retriedVisit } = this.state
    if (!hasError || gaveUp) return
    if (retriedVisit === this.props.visit) this.setState({ gaveUp: true })
    else this.setState({ hasError: false, retriedVisit: this.props.visit })
  }

  render() {
    if (this.state.gaveUp) return <AppErrorFallback />
    if (this.state.hasError) return null
    return this.props.children
  }
}

// No design frame for this state: plain and neutral on purpose.
function AppErrorFallback() {
  return (
    <div role="alert" className="min-h-[60vh] flex flex-col items-center justify-center gap-[12px] px-[16px] text-center">
      <p className="text-[16px] text-brand-dark">Something went wrong on this page.</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="h-[40px] px-[20px] rounded-[8px] bg-brand-primary text-white text-[14px] font-semibold"
      >
        Reload page
      </button>
    </div>
  )
}

export default function AppErrorBoundary({ children }) {
  const { key, pathname, search, hash } = useLocation()
  // HashRouter gives the first load and every hash typed in the address bar the key "default".
  const visit = `${key}|${pathname}${search}${hash}`
  return <Boundary visit={visit}>{children}</Boundary>
}
